import React, { useEffect, useRef, useState } from 'react';
import {
  listReportsForAdmin,
  loadReportContext,
  STATUS_FILTER_OPTIONS,
  DEFAULT_STATUS_FILTER,
  STALE_AFTER_HOURS,
  labelForReason,
  labelForReportStatus,
  pillClassForReportStatus,
  isStale,
  shortAge,
  isBusinessReport,
  isOrderReport,
  isPlatformReport,
} from '../lib/reports.js';
import './ReportsPage.css';

// ============================================================
// ReportsPage — Session B.
//
// READ-ONLY oversight of V1's member-reports-member queue, across every
// group. Resolution stays with group admins (subscribeToGroupReports +
// resolveReport in src/lib/db.js); this page exists so that Aangan can
// SEE what those admins are — or aren't — doing.
//
// The gap: V1's report sheet promises the reporter "Admins of this batch
// will review within 24 hours." If the group admin is the bad actor, or
// simply never opens the queue, that promise silently fails and nobody at
// Aangan can tell. Until Session B the /reports rule didn't even grant
// platform admins read access.
//
// The STALE badge is the point of the page: an OPEN report older than
// STALE_AFTER_HOURS is one nobody kept the promise on.
//
// LAYOUT
// ------
// Filter chips (Open by default) → table → click a row → detail drawer.
// Mirrors BusinessesPage's structure and reuses its sa-table / sa-filter-*
// / sa-detail-panel classes, so the panel stays visually coherent.
//
// COST
// ----
// Mount / filter change: 25 reads (one page).
// Load more:            25 reads per click.
// Open a row:           3 reads for a V1 member report (reporter, reported,
//                       group), 2 for a C1 business post (reporter,
//                       business), 4 for a C2a order chat (reporter,
//                       reported, business, order). Cached per report id for
//                       the life of the page. Names are NOT eagerly joined
//                       for the list — that would be ~75 extra reads per
//                       page for text nobody reads.
// ============================================================

function fmtFullTimestamp(ts) {
  if (!ts) return '—';
  let d;
  if (typeof ts?.toDate === 'function') d = ts.toDate();
  else if (ts instanceof Date) d = ts;
  else if (typeof ts?.seconds === 'number') d = new Date(ts.seconds * 1000);
  else return '—';
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Short UID for the table — full value stays in the title attribute and
// the drawer, so an admin can still copy it when debugging.
function shortUid(uid) {
  if (!uid) return '—';
  return uid.length > 10 ? `${uid.slice(0, 6)}…${uid.slice(-4)}` : uid;
}

// ============================================================
// Detail drawer
// ============================================================
function ReportDetailPanel({ report, context, contextLoading, onClose }) {
  if (!report) return null;

  const reporterName = context?.reporter?.name || null;
  const reportedName = context?.reported?.name || null;
  const groupName = context?.group?.name || null;
  const biz = isBusinessReport(report);
  const ord = isOrderReport(report);
  const businessName = context?.business?.name || null;
  const order = context?.order || null;

  // Which side filed it. Both parties to an order chat can report, so
  // "Reporter" alone is ambiguous in a way it never is for V1.
  const reporterRoleLabel = report.reporterRole === 'merchant' ? 'business' : 'customer';

  return (
    <>
      <div className="sa-drawer-backdrop" onClick={onClose} />
      <div className="sa-detail-panel open" role="dialog" aria-modal="true">
        <div className="sa-detail-panel-header">
          <div className="sa-detail-panel-title-block">
            <div className="sa-detail-panel-title">
              {labelForReason(report.reason)}
            </div>
            <div className="sa-detail-panel-sub">
              <span className={`sa-pill ${pillClassForReportStatus(report.status)}`}>
                {labelForReportStatus(report.status)}
              </span>
              {isStale(report) && (
                <span className="sa-report-stale-tag">
                  Unactioned {shortAge(report.createdAt)}
                </span>
              )}
            </div>
          </div>
          <button
            className="sa-detail-panel-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            ×
          </button>
        </div>

        <div className="sa-detail-panel-body">
          {contextLoading && (
            <div className="sa-detail-empty">Loading names…</div>
          )}

          <div className="sa-detail-block">
            <div className="sa-detail-block-title">Who</div>
            {biz ? (
              <>
                <div className="sa-detail-row">
                  <div className="sa-detail-row-label">Business</div>
                  <div className="sa-detail-row-value">
                    {businessName || <span className="sa-report-muted">deleted or unknown business</span>}
                    <div className="sa-report-uid" title={report.businessId}>
                      {report.businessId || '—'}
                    </div>
                  </div>
                </div>
                <div className="sa-detail-row">
                  <div className="sa-detail-row-label">Post</div>
                  <div className="sa-detail-row-value">
                    <div className="sa-report-uid" title={report.targetId}>
                      {report.targetId || '—'}
                    </div>
                  </div>
                </div>
                <div className="sa-detail-row">
                  <div className="sa-detail-row-label">Reporter</div>
                  <div className="sa-detail-row-value">
                    {reporterName || <span className="sa-report-muted">unknown user</span>}
                    <div className="sa-report-uid" title={report.reporterId}>
                      {report.reporterId || '—'}
                    </div>
                  </div>
                </div>
              </>
            ) : ord ? (
              <>
                <div className="sa-detail-row">
                  <div className="sa-detail-row-label">Reported</div>
                  <div className="sa-detail-row-value">
                    {reportedName || <span className="sa-report-muted">unknown user</span>}
                    <div className="sa-report-uid" title={report.reportedUserId}>
                      {report.reportedUserId || '—'}
                    </div>
                  </div>
                </div>
                <div className="sa-detail-row">
                  <div className="sa-detail-row-label">Reporter</div>
                  <div className="sa-detail-row-value">
                    {reporterName || <span className="sa-report-muted">unknown user</span>}
                    {' '}
                    <span className="sa-report-muted">(the {reporterRoleLabel})</span>
                    <div className="sa-report-uid" title={report.reporterId}>
                      {report.reporterId || '—'}
                    </div>
                  </div>
                </div>
                <div className="sa-detail-row">
                  <div className="sa-detail-row-label">Business</div>
                  <div className="sa-detail-row-value">
                    {businessName || <span className="sa-report-muted">deleted or unknown business</span>}
                    <div className="sa-report-uid" title={report.businessId}>
                      {report.businessId || '—'}
                    </div>
                  </div>
                </div>
                <div className="sa-detail-row">
                  <div className="sa-detail-row-label">Order</div>
                  <div className="sa-detail-row-value">
                    {order ? (
                      <>
                        #{order.displayNumber || order.id}
                        {order.status ? ` · ${order.status}` : ''}
                        {typeof order.total === 'number'
                          ? ` · ₹${order.total.toLocaleString('en-IN')}`
                          : ''}
                      </>
                    ) : (
                      <span className="sa-report-muted">deleted or unknown order</span>
                    )}
                    <div className="sa-report-uid" title={report.orderId}>
                      {report.orderId || '—'}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="sa-detail-row">
                  <div className="sa-detail-row-label">Reported</div>
                  <div className="sa-detail-row-value">
                    {reportedName || <span className="sa-report-muted">unknown user</span>}
                    <div className="sa-report-uid" title={report.reportedId}>
                      {report.reportedId || '—'}
                    </div>
                  </div>
                </div>
                <div className="sa-detail-row">
                  <div className="sa-detail-row-label">Reporter</div>
                  <div className="sa-detail-row-value">
                    {reporterName || <span className="sa-report-muted">unknown user</span>}
                    <div className="sa-report-uid" title={report.reporterId}>
                      {report.reporterId || '—'}
                    </div>
                  </div>
                </div>
                <div className="sa-detail-row">
                  <div className="sa-detail-row-label">Batch</div>
                  <div className="sa-detail-row-value">
                    {groupName || <span className="sa-report-muted">deleted or unknown batch</span>}
                    <div className="sa-report-uid" title={report.groupId}>
                      {report.groupId || '—'}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="sa-detail-block">
            <div className="sa-detail-block-title">What</div>
            <div className="sa-detail-row">
              <div className="sa-detail-row-label">Reason</div>
              <div className="sa-detail-row-value">{labelForReason(report.reason)}</div>
            </div>
            {report.detail ? (
              <div className="sa-report-detail-quote">{report.detail}</div>
            ) : (
              <div className="sa-detail-empty">No additional detail was given.</div>
            )}
          </div>

          <div className="sa-detail-block">
            <div className="sa-detail-block-title">Resolution</div>
            <div className="sa-detail-row">
              <div className="sa-detail-row-label">Filed</div>
              <div className="sa-detail-row-value">{fmtFullTimestamp(report.createdAt)}</div>
            </div>
            <div className="sa-detail-row">
              <div className="sa-detail-row-label">Status</div>
              <div className="sa-detail-row-value">{labelForReportStatus(report.status)}</div>
            </div>
            {report.status === 'open' ? (
              <div className="sa-report-note">
                {biz
                  ? 'A reported business post. Act via the business\'s page (suspend / contact the owner). Reports older than ' + STALE_AFTER_HOURS + 'h are flagged.'
                  : ord
                  ? 'A reported order conversation. Act via the business\'s page (suspend / contact the owner). Reports older than ' + STALE_AFTER_HOURS + 'h are flagged.'
                  : 'Still awaiting a batch admin. Reports older than ' + STALE_AFTER_HOURS + 'h are flagged unactioned — V1 tells reporters to expect a review within 24 hours.'}
              </div>
            ) : (
              <>
                <div className="sa-detail-row">
                  <div className="sa-detail-row-label">Reviewed</div>
                  <div className="sa-detail-row-value">{fmtFullTimestamp(report.reviewedAt)}</div>
                </div>
                <div className="sa-detail-row">
                  <div className="sa-detail-row-label">Reviewer</div>
                  <div className="sa-detail-row-value">
                    <div className="sa-report-uid" title={report.reviewerId}>
                      {report.reviewerId || '—'}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="sa-report-readonly-note">
            {biz
              ? 'Business post report. Review the post on the business\'s profile; use the Businesses page to suspend the listing or contact the owner if action is needed.'
              : ord
              ? 'Order chat report. The conversation is private to the two parties — it is not readable from this panel. Use the Businesses page to suspend the listing, or contact either party directly, if action is needed.'
              : 'Read-only. Batch admins resolve their own reports — dismiss, warn, or remove — from the V1 moderation queue.'}
            {isPlatformReport(report) && (
              <>
                {' '}Reports of this kind have no in-panel resolve action yet, so they stay Open — see the note on the /reports update rule in firestore.rules.
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================
// Page
// ============================================================
export default function ReportsPage() {
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);
  const [reports, setReports] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(null);
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);

  // Cache resolved names per report id so reopening a row costs 0 reads.
  const contextCache = useRef({});

  // (Re)load page one whenever the filter changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setReports([]);
    setLastDoc(null);
    setHasMore(false);

    listReportsForAdmin({ statusFilter, cursor: null })
      .then((res) => {
        if (cancelled) return;
        setReports(res.reports);
        setLastDoc(res.lastDoc);
        setHasMore(res.hasMore);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[ReportsPage] load failed:', err);
        setError(err?.message || 'Failed to load reports');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [statusFilter]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore || !lastDoc) return;
    setLoadingMore(true);
    try {
      const res = await listReportsForAdmin({ statusFilter, cursor: lastDoc });
      setReports((prev) => [...prev, ...res.reports]);
      setLastDoc(res.lastDoc);
      setHasMore(res.hasMore);
    } catch (err) {
      console.error('[ReportsPage] load-more failed:', err);
      setError(err?.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleOpen = async (report) => {
    setSelected(report);

    const cached = contextCache.current[report.id];
    if (cached) {
      setContext(cached);
      return;
    }

    setContext(null);
    setContextLoading(true);
    try {
      const ctx = await loadReportContext(report);
      contextCache.current[report.id] = ctx;
      setContext(ctx);
    } catch (err) {
      console.error('[ReportsPage] context load failed:', err);
      setContext({ reporter: null, reported: null, group: null, business: null, order: null });
    } finally {
      setContextLoading(false);
    }
  };

  const staleCount = reports.filter(isStale).length;

  // Open reports split by who is actually on the hook. V1 member reports wait
  // on that batch's admin; C1/C2a reports have no batch and no admin but
  // Aangan — and, until the resolve path exists, nothing at all. Counting them
  // together under "awaiting a batch admin" pointed at the wrong person and
  // implied the V2 rows were someone else's problem.
  const v1OpenCount = reports.filter(
    (r) => r.status === 'open' && !isPlatformReport(r)
  ).length;
  const platformOpenCount = reports.filter(
    (r) => r.status === 'open' && isPlatformReport(r)
  ).length;

  return (
    <div className="sa-reports-page">
      {/* Filter chips */}
      <div className="sa-filter-bar">
        {STATUS_FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.label}
            type="button"
            className={`sa-filter-chip ${statusFilter === opt.value ? 'active' : ''}`}
            onClick={() => setStatusFilter(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Stale banner — the reason this page exists. Only shown when the
          current page actually contains unactioned reports. */}
      {!loading && staleCount > 0 && (
        <div className="sa-report-stale-banner">
          <b>{staleCount} report{staleCount === 1 ? '' : 's'} unactioned</b> for more
          than {STALE_AFTER_HOURS} hours.
          {v1OpenCount > 0 && ' Batch admins were told to review within 24.'}
          {platformOpenCount > 0 && ' Business and order reports have no batch admin — they are Aangan\'s to action.'}
        </div>
      )}

      {error && (
        <div className="sa-error-banner">
          <b>Couldn't load reports.</b> {error}
        </div>
      )}

      <div className="sa-table-wrap">
        <table className="sa-table">
          <thead>
            <tr>
              <th>Reported</th>
              <th>Reason</th>
              {/* Not "Batch": this column carries the report's scope, and only
                  V1 member reports are scoped to a batch. C1/C2a rows show the
                  businessId here, which read as a batch code under the old
                  header — misleading for a table batch admins also read. */}
              <th>Batch / business</th>
              <th>Filed</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="sa-table-empty">Loading…</td>
              </tr>
            )}

            {!loading && reports.length === 0 && !error && (
              <tr>
                <td colSpan={5} className="sa-table-empty">
                  {statusFilter === 'open'
                    ? 'No open reports. Nothing is waiting on a batch admin.'
                    : 'No reports match this filter.'}
                </td>
              </tr>
            )}

            {!loading && reports.map((r) => (
              <tr
                key={r.id}
                onClick={() => handleOpen(r)}
                className={isStale(r) ? 'sa-report-row-stale' : ''}
              >
                <td>
                  {isBusinessReport(r) ? (
                    <span title={r.businessId}>🏪 business post</span>
                  ) : isOrderReport(r) ? (
                    <span title={r.reportedUserId}>💬 order chat</span>
                  ) : (
                    <div className="sa-report-uid" title={r.reportedId}>
                      {shortUid(r.reportedId)}
                    </div>
                  )}
                </td>
                <td>{labelForReason(r.reason)}</td>
                <td>
                  {isPlatformReport(r) ? (
                    <div className="sa-report-uid" title={r.businessId}>
                      {shortUid(r.businessId)}
                    </div>
                  ) : (
                    <div className="sa-report-uid" title={r.groupId}>
                      {shortUid(r.groupId)}
                    </div>
                  )}
                </td>
                <td>
                  {shortAge(r.createdAt)}
                  {isStale(r) && <span className="sa-report-stale-dot" title="Unactioned" />}
                </td>
                <td>
                  <span className={`sa-pill ${pillClassForReportStatus(r.status)}`}>
                    {labelForReportStatus(r.status)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && hasMore && (
        <div className="sa-load-more-wrap">
          <button className="sa-btn" onClick={handleLoadMore} disabled={loadingMore} type="button">
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {!loading && reports.length > 0 && (
        <div className="sa-report-footnote">
          Showing {reports.length} report{reports.length === 1 ? '' : 's'}
          {statusFilter === 'open' && v1OpenCount > 0 && ` · ${v1OpenCount} awaiting a batch admin`}
          {statusFilter === 'open' && platformOpenCount > 0 && ` · ${platformOpenCount} for Aangan to action`}
        </div>
      )}

      {selected && (
        <ReportDetailPanel
          report={selected}
          context={context}
          contextLoading={contextLoading}
          onClose={() => { setSelected(null); setContext(null); }}
        />
      )}
    </div>
  );
}
